import type {
  FormSubmittedNotificationData,
  Notification,
  NotificationSender,
  NotificationTypeName,
  RowCommentNotificationData,
  RowNotificationData,
  TenantWorkspaceAwaitingAdminData,
  VersionUpgradeNotificationData,
  WebhookNotificationData,
} from './types'

/**
 * What one notification says.
 *
 * The title is an i18n key and its values; the body is text the notification
 * carries -- a comment, a form's answers -- and is rendered verbatim rather than
 * translated, because it is the user's own words and not a message of ours.
 */
export interface NotificationContent {
  key: NotificationKey
  values: Record<string, string | number>
  body?: string
}

/**
 * The message keys a notification can carry.
 *
 * Closed rather than `string`, so `t(content.key, …)` type-checks against the
 * catalogue: a key renamed in `en.json` and not here is a compile error rather
 * than a `[missing "en…"]` string sitting in the panel.
 */
export type NotificationKey =
  | 'notification.collaboratorAddedToRow'
  | 'notification.formSubmitted'
  | 'notification.rowCommentMentioned'
  | 'notification.tenantWorkspaceAwaitingAdmin'
  | 'notification.unknown'
  | 'notification.userMentionInRichTextField'
  | 'notification.versionUpgrade'
  | 'notification.webhookDeactivated'
  | 'notification.webhookPayloadTooLarge'

type Renderer = (notification: Notification) => NotificationContent

/** Who sent it, for a sentence. `null` for the ones the system raises itself. */
function senderName(sender: NotificationSender | null | undefined): string {
  if (sender === null || sender === undefined) return ''
  const first = sender.first_name
  return first !== undefined && first !== '' ? first : sender.username
}

function asRow(notification: Notification): RowNotificationData {
  return notification.data as RowNotificationData
}

/**
 * One entry per registered notification type.
 *
 * `satisfies` rather than a type annotation, so the map is checked for
 * exhaustiveness against `NotificationTypeName` while each entry keeps its own
 * narrowed `data`. Adding a type to the union without adding it here is a
 * compile error rather than a `[missing "en…"]` string in the panel.
 */
export const NOTIFICATION_RENDERERS = {
  row_comment_mentioned: (notification) => {
    const data = notification.data as RowCommentNotificationData
    return {
      key: 'notification.rowCommentMentioned',
      values: {
        sender: senderName(notification.sender),
        row: data.row_name,
        table: data.table_name,
      },
      body: data.message,
    }
  },

  user_mention_in_rich_text_field: (notification) => {
    const data = asRow(notification)
    return {
      key: 'notification.userMentionInRichTextField',
      values: {
        sender: senderName(notification.sender),
        field: data.field_name ?? '',
        row: data.row_name,
        table: data.table_name,
      },
    }
  },

  collaborator_added_to_row: (notification) => {
    const data = asRow(notification)
    return {
      key: 'notification.collaboratorAddedToRow',
      values: {
        sender: senderName(notification.sender),
        row: data.row_name,
        table: data.table_name,
      },
    }
  },

  form_submitted: (notification) => {
    const data = notification.data as FormSubmittedNotificationData
    return {
      key: 'notification.formSubmitted',
      values: { form: data.form_name, table: data.table_name },
      // Each answer on its own line. The values are whatever was typed into the
      // form, so they are shown rather than interpolated into a sentence.
      body: data.values.map(([field, value]) => `${field}: ${value}`).join('\n'),
    }
  },

  webhook_deactivated: (notification) => {
    const data = notification.data as WebhookNotificationData
    return {
      key: 'notification.webhookDeactivated',
      values: { webhook: data.webhook_name },
    }
  },

  webhook_payload_too_large: (notification) => {
    const data = notification.data as WebhookNotificationData
    return {
      key: 'notification.webhookPayloadTooLarge',
      values: { webhook: data.webhook_name, limit: data.batch_limit ?? 0 },
    }
  },

  baserow_version_upgrade: (notification) => {
    const data = notification.data as VersionUpgradeNotificationData
    return {
      key: 'notification.versionUpgrade',
      values: { version: data.version },
    }
  },

  tenant_workspace_awaiting_admin: (notification) => {
    const data = notification.data as TenantWorkspaceAwaitingAdminData
    return {
      key: 'notification.tenantWorkspaceAwaitingAdmin',
      values: { workspace: data.workspace_name, tenant: data.tenant },
    }
  },
} satisfies Record<NotificationTypeName, Renderer>

/**
 * What a notification says, whatever it is.
 *
 * An unrecognised type is rendered rather than dropped. The list is a record of
 * things that happened to this user, and a type this build does not know about --
 * one left behind by a version that did, or by an application being removed --
 * is still something they were told. Showing its name beats showing nothing at
 * all, which reads as a bug in the panel rather than as a gap in the build.
 */
export function renderNotification(notification: Notification): NotificationContent {
  const renderer = (NOTIFICATION_RENDERERS as Partial<Record<string, Renderer>>)[
    notification.type
  ]

  if (renderer === undefined) {
    return { key: 'notification.unknown', values: { type: notification.type } }
  }
  return renderer(notification)
}
